import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight, UserRound } from 'lucide-react';
import './Hyperlink.css';

/**
 * Hyperlink Component
 * 
 * Uses design system typography classes:
 * - typo-link-lg (large)
 * - typo-link (regular)
 * - typo-link-sm (small)
 * - typo-link-editorial-lg (editorial-large)
 * - typo-link-editorial (editorial-regular)
 * - typo-h6 (label)
 * 
 * Colors from tokens:
 * - --color-gray-10 (default)
 * - --color-brand-primary-hover (hover)
 * - --color-brand-primary (focus)
 * - --color-gray-6 (disabled — Figma 19:133; AA on white, 4.9:1)
 */

// Map variants to typography classes
const TYPOGRAPHY_CLASSES = {
  large: 'typo-link-lg',
  regular: 'typo-link',
  small: 'typo-link-sm',
  'editorial-large': 'typo-link-editorial-lg',
  'editorial-regular': 'typo-link-editorial',
  label: 'typo-h6',
};

// Variants that should have animated underline
const UNDERLINE_VARIANTS = ['large', 'regular', 'small', 'editorial-large'];

const Hyperlink = ({
  children,
  href = '#',
  variant = 'regular',
  iconLeft = null,
  iconRight = null,
  showIconLeft = false,
  showIconRight = false,
  disabled = false,
  inline = false,
  className = '',
  onClick,
  ...props
}) => {
  // Get typography class from map
  const typographyClass = TYPOGRAPHY_CLASSES[variant] || TYPOGRAPHY_CLASSES.regular;
  
  // Check if this variant has underline
  const hasUnderline = UNDERLINE_VARIANTS.includes(variant);

  // Handle click
  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  // Render icon
  const renderIcon = (icon, position) => {
    if (!icon && position === 'left' && !showIconLeft) return null;
    if (!icon && position === 'right' && !showIconRight) return null;

    const DefaultIcon = position === 'left' ? UserRound : ArrowRight;
    
    return (
      <span className="hyperlink__icon">
        {icon || <DefaultIcon />}
      </span>
    );
  };

  const classes = [
    'hyperlink',
    typographyClass,
    `hyperlink--${variant}`,
    hasUnderline && 'hyperlink--has-underline',
    disabled && 'hyperlink--disabled',
    inline && 'hyperlink--inline',
    className
  ].filter(Boolean).join(' ');

  return (
    <a
      href={disabled ? undefined : href}
      className={classes}
      onClick={handleClick}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      {...props}
    >
      {renderIcon(iconLeft, 'left')}
      
      <span className="hyperlink__text">
        {children}
      </span>
      
      {renderIcon(iconRight, 'right')}
      
      {hasUnderline && <span className="hyperlink__underline" />}
    </a>
  );
};

Hyperlink.propTypes = {
  /** Link text content */
  children: PropTypes.node.isRequired,
  /** URL destination */
  href: PropTypes.string,
  /** Hyperlink variant */
  variant: PropTypes.oneOf([
    'large',
    'regular', 
    'small',
    'editorial-large',
    'editorial-regular',
    'label'
  ]),
  /** Custom left icon component */
  iconLeft: PropTypes.node,
  /** Custom right icon component */
  iconRight: PropTypes.node,
  /** Show default left icon (User) */
  showIconLeft: PropTypes.bool,
  /** Show default right icon (ArrowRight) */
  showIconRight: PropTypes.bool,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** A link inside running text: sits on the text baseline instead of as a flex row */
  inline: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Click handler */
  onClick: PropTypes.func,
};

export default Hyperlink;
